// Row 105 AckSummary (`acksum`, intake I-17, workbench W-12): the *Panel acknowledgment summary*. A head of G3's Ring
// (accepted n of N, its name carrying the count) and three Kpi counts — *Accepted* · *Acknowledgment Pending* ·
// *Declined* (a nonresponse is still pending) — then one core Row per interviewer: name and round · the values and
// skills they assess as chips · the response pill (#99's words and tones) · when they responded · and on a Declined
// row only the small *Panel change* button that opens the panel-change request (absent for a seat without it, DC-24).

import { useId, type MouseEvent } from "react";
import { Button, Chip, Chips, Pill, Row, RowName, type Blocked } from "../core";
import { ACKNOWLEDGMENT_STATUS } from "../labels";
import { cx } from "../lib/cx";
import { formatDateTime, toEpoch, type Instant } from "../lib/time";
import { VisuallyHidden } from "../lib/VisuallyHidden";
import { Kpi, KpiRow, Ring } from "../g3";
import { ACKSUM_LABELS, G8_TEMPLATES } from "./labels";
import { RESPONSE_PILL, type ResponseStatus } from "./Responses";
import type { Hs } from "../lib/hs";

export interface AckSummaryRow {
  key: string;
  /** The interviewer (data). */
  name: string;
  /** The round's name. */
  round: string;
  /** The values and skills assigned (his words). */
  assigned?: readonly string[] | undefined;
  status: ResponseStatus;
  respondedAt?: Instant | undefined;
  /** A Declined row's *Panel change* action. */
  onPanelChange?: ((event: MouseEvent<HTMLButtonElement>) => void) | undefined;
  /** false: the seat may not open a panel change; the action is absent. */
  canPanelChange?: boolean | undefined;
  panelChangeBlocked?: Blocked | undefined;
  hs?: Hs | undefined;
}

export interface AckSummaryProps {
  rows: readonly AckSummaryRow[];
  /** The heading (default *Panel acknowledgment summary*). */
  title?: string | undefined;
  timeZone?: string | undefined;
  loading?: boolean | undefined;
  can?: boolean | undefined;
  className?: string | undefined;
}

export function AckSummary({ rows, title = ACKSUM_LABELS.panelAcknowledgmentSummary, timeZone, loading, can, className }: AckSummaryProps) {
  const id = useId();
  if (can === false) return null;
  const accepted = rows.filter((row) => row.status === "accepted").length;
  const declined = rows.filter((row) => row.status === "declined").length;
  const pending = rows.length - accepted - declined;
  const ringName = G8_TEMPLATES.acceptedOf({ n: accepted, total: rows.length });
  return (
    <section className={cx("mc-acksum", className)} aria-labelledby={`${id}-t`} aria-busy={loading ? true : undefined}>
      <h4 className="mc-acksum-t" id={`${id}-t`}>{title}</h4>
      <div className="mc-acksum-h">
        <Ring label={ringName} n={accepted} of={rows.length} band={declined > 0 ? "flag" : accepted === rows.length ? "ok" : "info"} size={56} />
        <KpiRow className="mc-acksum-k">
          <Kpi label={ACKNOWLEDGMENT_STATUS.accepted} value={accepted} loading={loading} />
          <Kpi label={ACKNOWLEDGMENT_STATUS.acknowledgmentPending} value={pending} loading={loading} />
          <Kpi label={ACKNOWLEDGMENT_STATUS.declined} value={declined} loading={loading} />
        </KpiRow>
      </div>
      <div className="mc-acksum-rows">
        {loading
          ? Array.from({ length: Math.max(rows.length, 3) }, (_, index) => <Row key={index} lead="28%" cols={5} loading />)
          : rows.map((row) => {
            const pill = RESPONSE_PILL[row.status];
            return (
              <Row key={row.key} lead="28%" cols={5} className="mc-acksum-row">
                <RowName name={row.name} sub={row.round} />
                <Chips>{(row.assigned ?? []).map((word) => <Chip key={word} sm>{word}</Chip>)}</Chips>
                <Pill tone={pill.tone}>{pill.word}</Pill>
                {row.respondedAt !== undefined ? (
                  <span className="mc-acksum-at">
                    <VisuallyHidden>{ACKSUM_LABELS.responded}{" "}</VisuallyHidden>
                    <time dateTime={new Date(toEpoch(row.respondedAt)).toISOString()}>{formatDateTime(row.respondedAt, timeZone)}</time>
                  </span>
                ) : <span className="mc-t3">{"—"}</span>}
                {row.status === "declined" ? (
                  <Button sm can={row.canPanelChange !== false} blocked={row.panelChangeBlocked} onClick={row.onPanelChange} hs={row.hs}>
                    {ACKSUM_LABELS.panelChange}
                  </Button>
                ) : <span />}
              </Row>
            );
          })}
      </div>
    </section>
  );
}
